import React, { useState, useEffect } from "react";
import {
  User, Package, Heart, LogOut, ChevronRight, Settings, Save, Edit3,
  CheckCircle2, AlertCircle, Trash2, ShieldCheck, Truck, ShoppingCart, Sun, Moon, Clock, Box, DownloadCloud, Smartphone
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useTheme } from "../context/ThemeContext";
import axios from "axios";
import SEO from '../components/SEO';

const Profile = () => {
  const { user, logout } = useAuth();
  const { wishlistItems, addToCart } = useCart();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("profile");
  const [isEditing, setIsEditing] = useState(false);
  const [orders, setOrders] = useState([]);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");

  // 🔥 UPDATED INSTALL LOGIC STATES
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);

  const [userData, setUserData] = useState({
    name: user?.username || user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    address: user?.address || "",
  });

  // 🔥 MASTER EFFECT: CAPTURE INSTALL PROMPT
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      console.log("✅ PWA: Install prompt captured and ready!");
      e.preventDefault(); // Default browser popup ko roko
      setDeferredPrompt(e); // Event ko state mein save karo
    };

    const handleAppInstalled = () => {
      console.log("✅ PWA: App successfully installed");
      setIsAppInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    // Check if already in standalone mode
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true) {
      setIsAppInstalled(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  // 🔥 UPDATED INSTALL CLICK HANDLER
  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Agar event capture nahi hua toh manual instruction dikhao
      showPopup("Browser prompt not ready. Use Chrome menu > Install App", "info");
      return;
    }
    
    // Asli browser popup dikhao
    deferredPrompt.prompt();
    
    // User ka choice wait karo
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`👤 User choice: ${outcome}`);
    
    if (outcome === "accepted") {
      setIsAppInstalled(true);
      setDeferredPrompt(null);
    }
  };

  const getAuthHeaders = () => {
    const myToken = user?.accessToken || user?.token;
    return {
      token: `Bearer ${myToken}`,
      Authorization: `Bearer ${myToken}`
    };
  };

  const fetchOrders = async () => {
    if (!user?._id) return;
    try {
      const res = await axios.get(`https://wethrift-backend.onrender.com/api/orders/find/${user._id}`, {
        headers: getAuthHeaders()
      });
      if (res.data) setOrders(res.data);
    } catch (err) {
      console.error("Order Fetch Error:", err.response?.data || err.message);
    }
  };

  useEffect(() => {
    if (activeTab === "orders") fetchOrders();
  }, [activeTab]);

  const showPopup = (msg, type = "success") => {
    setToast({ show: true, message: msg, type });
    setTimeout(() => setToast({ show: false, message: "", type: "success" }), 3000);
  };

  const handleSave = async () => {
    try {
      const res = await axios.put(`https://wethrift-backend.onrender.com/api/users/${user._id}`, userData, {
        headers: getAuthHeaders()
      });
      if (res.data) {
        const updatedUser = { ...user, ...res.data };
        localStorage.setItem("weThriftUser", JSON.stringify(updatedUser));
        showPopup("Identity Updated!");
        setIsEditing(false);
      }
    } catch (err) { showPopup("Update Failed!", "error"); }
  };

  const handleDeleteAccount = async () => {
    try {
      await axios.delete(`https://wethrift-backend.onrender.com/api/users/${user._id}`, {
        headers: getAuthHeaders()
      });
      showPopup("Account Terminated");
      setTimeout(() => { logout(); navigate("/"); }, 2000);
    } catch (err) { showPopup("Termination Failed", "error"); }
  };

  const tabs = [
    { id: "profile", label: "Identity", icon: <User size={18} /> },
    { id: "orders", label: "Orders", icon: <Package size={18} /> },
    { id: "wishlist", label: "Archive", icon: <Heart size={18} /> },
    { id: "settings", label: "Security", icon: <Settings size={18} /> },
  ];

  if (!user) return null;

  return (
    <>
      <SEO 
        title={`${userData.name.split(" ")[0] || "User"}'s Vault | Profile`} 
        description="Manage your identity, track your vintage acquisitions, and view your archived grails."
        url="/profile"
      />

      <div className={`min-h-screen pt-20 md:pt-28 pb-20 font-sans transition-colors duration-500 ${isDarkMode ? 'bg-[#050505] text-white' : 'bg-zinc-50 text-black'}`}>
        
        {toast.show && (
          <div className={`fixed top-10 right-4 md:top-24 md:right-10 z-[100] flex items-center gap-3 px-4 md:px-6 py-4 rounded-2xl border shadow-2xl animate-in fade-in slide-in-from-top-4 ${isDarkMode ? 'bg-zinc-900 border-white/10' : 'bg-white border-black/10'}`}>
            {toast.type === "success" ? <CheckCircle2 size={18} className="text-emerald-500" /> : <AlertCircle size={18} className="text-red-500" />}
            <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-white' : 'text-black'}`}>{toast.message}</p>
          </div>
        )}

        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className={`flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-10 md:mb-16 border-b pb-10 ${isDarkMode ? 'border-zinc-900' : 'border-zinc-200'}`}>
            <div className="w-full md:w-auto">
              <p className="text-[9px] md:text-[10px] font-black text-zinc-500 uppercase tracking-[0.4em] mb-4 italic">User_Manifest // {user._id?.slice(-8).toUpperCase()}</p>
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-black italic tracking-tighter uppercase leading-none">
                {userData.name.split(" ")[0] || "User"} <span className={isDarkMode ? 'text-zinc-800' : 'text-zinc-300'}>Archive</span>
              </h1>
            </div>
            <button onClick={logout} className="w-full md:w-auto flex items-center justify-center gap-3 bg-red-600/5 hover:bg-red-600 text-red-500 hover:text-white border border-red-600/20 px-8 py-4 rounded-2xl transition-all text-[10px] font-black uppercase tracking-widest">
              <LogOut size={16} /> Logout
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8 md:gap-12">
            {/* Sidebar */}
            <div className="flex lg:flex-col overflow-x-auto lg:overflow-visible no-scrollbar gap-3 pb-4 lg:pb-0 sticky top-24 z-40 bg-inherit">
              {tabs.map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex-shrink-0 lg:w-full flex items-center justify-between p-4 md:p-6 rounded-2xl border transition-all font-black uppercase italic text-[10px] tracking-[0.2em] ${activeTab === tab.id ? (isDarkMode ? "bg-white text-black border-white" : "bg-black text-white border-black") : (isDarkMode ? "bg-zinc-900/10 text-zinc-500 border-zinc-800/50" : "bg-zinc-200/50 text-zinc-500 border-zinc-300")}`}>
                  <div className="flex items-center gap-3 md:gap-4">{tab.icon} {tab.label}</div>
                  <ChevronRight size={14} className="hidden lg:block" />
                </button>
              ))}
            </div>

            {/* Content Area */}
            <div className={`rounded-[2rem] md:rounded-[3rem] p-6 md:p-10 border transition-all ${isDarkMode ? 'bg-zinc-900/20 border-zinc-900' : 'bg-white border-zinc-200 shadow-xl'}`}>
              
              {activeTab === "profile" && (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <h3 className="text-3xl md:text-4xl font-black uppercase italic tracking-tighter text-emerald-500">Identity_Protocol</h3>
                    <button onClick={() => (isEditing ? handleSave() : setIsEditing(true))} className={`w-full md:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-2xl font-black text-[10px] uppercase transition-all ${isEditing ? "bg-emerald-500 text-white" : (isDarkMode ? "bg-zinc-800/50 text-zinc-400" : "bg-zinc-100 text-zinc-500")}`}>
                      {isEditing ? <><Save size={14} /> Commit Changes</> : <><Edit3 size={14} /> Update Info</>}
                    </button>
                  </div>
                  
                  {/* 🔥 IMPROVED INSTALL APP BANNER */}
                  {!isAppInstalled && (
                    <div className={`p-6 md:p-8 rounded-[2rem] border-2 border-dashed flex flex-col md:flex-row items-center justify-between gap-6 transition-all ${isDarkMode ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-zinc-50 border-zinc-200'}`}>
                      <div className="flex items-center gap-5">
                        <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center text-emerald-500 animate-pulse">
                          <Smartphone size={28} />
                        </div>
                        <div>
                          <h4 className="text-lg font-black uppercase italic tracking-tighter">Mobile_Archive_V1</h4>
                          <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mt-1 italic">Fast track access // Offline support</p>
                        </div>
                      </div>
                      <button 
                        onClick={handleInstallClick} 
                        className={`w-full md:w-auto flex items-center justify-center gap-3 px-10 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-lg transition-all ${deferredPrompt ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-emerald-500/20' : 'bg-zinc-500/20 text-zinc-500 cursor-not-allowed'}`}
                      >
                        <DownloadCloud size={16} /> {deferredPrompt ? 'Install Now' : 'App Ready'}
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    {Object.keys(userData).map((key) => (
                      <div key={key} className="space-y-3">
                        <label className="text-[9px] font-black uppercase text-zinc-500 tracking-[0.2em] ml-2 italic">{key.replace("_", " ")}</label>
                        <input disabled={!isEditing} value={userData[key]} onChange={(e) => setUserData({ ...userData, [key]: e.target.value })} className={`w-full border p-4 md:p-5 rounded-2xl text-sm font-bold outline-none transition-all ${isDarkMode ? 'bg-zinc-800/10 border-zinc-800 text-white' : 'bg-zinc-50 border-zinc-200 text-black'} ${isEditing ? 'border-emerald-500 ring-4 ring-emerald-500/10' : 'opacity-60'}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === "orders" && (
                <div className="space-y-8 animate-in fade-in duration-500">
                  <h3 className="text-3xl md:text-4xl font-black uppercase italic tracking-tighter text-emerald-500">Deployment_Logs</h3>
                  {orders.length === 0 ? (
                    <div className="text-center py-20 bg-zinc-500/5 rounded-3xl border border-dashed border-zinc-500/20">
                      <Package size={40} className="mx-auto mb-4 text-zinc-700 opacity-20" />
                      <p className="uppercase text-[9px] font-black tracking-widest text-zinc-600">No active deployments</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {orders.map((order) => (
                        <div key={order._id} className={`border rounded-[2.5rem] overflow-hidden transition-all ${isDarkMode ? 'bg-zinc-950 border-white/5' : 'bg-zinc-50 border-black/5'}`}>
                          <div className="px-6 py-4 bg-emerald-500/5 border-b border-white/5 flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <Clock size={12} className="text-emerald-500" />
                              <span className="text-[9px] font-black uppercase tracking-widest text-emerald-500">{order.status}</span>
                            </div>
                            <p className="text-[10px] font-black text-white bg-zinc-800 px-3 py-1 rounded-lg uppercase tracking-tighter italic border border-white/5">
                              {order.orderNumber || `WTH-${order._id.slice(-6).toUpperCase()}`}
                            </p>
                          </div>
                          <div className="p-6 md:p-8 flex flex-col md:flex-row gap-8 items-center">
                            <div className="flex -space-x-4 overflow-hidden">
                              {order.products.slice(0, 3).map((p, i) => (
                                <img key={i} src={p.productId?.img || p.img} className="w-16 h-20 object-cover rounded-xl border-2 border-black grayscale hover:grayscale-0 transition-all" alt="product" crossOrigin="anonymous" />
                              ))}
                            </div>
                            <div className="flex-1 text-center md:text-left">
                              <p className="text-2xl font-black italic tracking-tighter text-inherit">₹{order.amount.toLocaleString()}</p>
                              <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mt-1 italic">{order.products.length} Items // {order.payment_method || "GATEWAY_AUTH"}</p>
                            </div>
                            <Link to={`/track/${order._id}`} className={`px-10 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${isDarkMode ? 'bg-white text-black hover:bg-emerald-500 hover:text-white' : 'bg-black text-white hover:bg-emerald-600'}`}>Track_Package</Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "wishlist" && (
                <div className="animate-in fade-in duration-500">
                  <h3 className="text-3xl md:text-4xl font-black uppercase italic tracking-tighter mb-8 text-emerald-500">Archived_Grails</h3>
                  {wishlistItems.length === 0 ? (
                    <div className="text-center py-20">
                      <Heart size={40} className="mx-auto mb-4 text-zinc-800 opacity-20" />
                      <p className="uppercase text-[9px] font-black tracking-widest text-zinc-600">Vault is empty</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
                      {wishlistItems.map((item, index) => (
                        <div key={item._id || `wish-${index}`} className={`border rounded-[2rem] p-4 group transition-all ${isDarkMode ? 'bg-zinc-900/40 border-white/5' : 'bg-zinc-50 border-black/5'}`}>
                          <div className="relative aspect-[3/4] overflow-hidden rounded-2xl mb-4 bg-zinc-800">
                            <img src={item.img || item.image} alt="" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" crossOrigin="anonymous" />
                            <button onClick={() => addToCart(item)} className="absolute bottom-4 left-4 right-4 py-3 rounded-xl bg-white text-black opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all font-black uppercase text-[9px] flex items-center justify-center gap-2"><ShoppingCart size={12} /> Add to Cart</button>
                          </div>
                          <p className="text-[10px] font-black uppercase truncate text-zinc-400">{item.name || item.title}</p>
                          <p className="text-sm font-black italic tracking-tighter">₹{item.price}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === "settings" && (
                <div className="space-y-8 animate-in fade-in duration-500">
                  <h3 className="text-3xl md:text-4xl font-black uppercase italic tracking-tighter text-emerald-500">Security_Node</h3>
                  
                  <div className={`flex items-center justify-between p-6 border rounded-3xl ${isDarkMode ? 'bg-zinc-800/20 border-white/5' : 'bg-zinc-50 border-black/5'}`}>
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-purple-500/10 rounded-xl text-purple-500">
                        {isDarkMode ? <Moon size={20} /> : <Sun size={20} />}
                      </div>
                      <p className="text-[10px] font-black uppercase tracking-widest">Display Protocol</p>
                    </div>
                    <button onClick={toggleTheme} className={`px-6 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${isDarkMode ? 'bg-white text-black' : 'bg-black text-white'}`}>
                      {isDarkMode ? 'Light' : 'Dark'}
                    </button>
                  </div>

                  <div className={`flex items-center justify-between p-6 border rounded-3xl ${isDarkMode ? 'bg-zinc-800/20 border-white/5' : 'bg-zinc-50 border-black/5'}`}>
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-emerald-500/10 rounded-xl text-emerald-500">
                        <Smartphone size={20} />
                      </div>
                      <p className="text-[10px] font-black uppercase tracking-widest">Native Deployment</p>
                    </div>
                    <span className={`text-[8px] font-black uppercase px-3 py-1 rounded-full ${isAppInstalled ? 'bg-emerald-500/20 text-emerald-500' : 'bg-zinc-500/10 text-zinc-500'}`}>
                      {isAppInstalled ? 'Installed' : 'Cloud_Only'}
                    </span>
                  </div>

                  <div className="p-8 border border-red-500/10 bg-red-500/5 rounded-3xl">
                    <p className="text-[10px] font-black text-red-500 uppercase tracking-widest flex items-center gap-2 mb-6"><ShieldCheck size={14} /> Danger_Zone</p>
                    <button onClick={() => setShowDeleteModal(true)} className="w-full bg-red-600 text-white py-5 rounded-2xl text-[10px] font-black uppercase tracking-[0.3em] hover:bg-red-700 transition-all">Terminate Account</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {showDeleteModal && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 backdrop-blur-md bg-black/80">
            <div className={`max-w-md w-full rounded-[2.5rem] p-8 border ${isDarkMode ? 'bg-[#0f0f0f] border-white/10' : 'bg-white border-black/10'}`}>
              <h3 className="text-2xl font-black uppercase italic tracking-tighter mb-2 text-red-500">Confirm Termination?</h3>
              <div className="space-y-3 my-6">
                {["Privacy", "Inactive", "Other"].map((reason) => (
                  <label key={reason} className={`flex items-center gap-3 p-4 rounded-2xl border cursor-pointer ${deleteReason === reason ? 'border-emerald-500 bg-emerald-500/5' : 'border-transparent bg-zinc-500/5'}`}>
                    <input type="radio" name="reason" onChange={() => setDeleteReason(reason)} />
                    <span className="text-[11px] font-black uppercase tracking-wider">{reason}</span>
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <button onClick={() => setShowDeleteModal(false)} className="py-4 rounded-2xl text-[10px] font-black uppercase bg-zinc-800 text-white">Cancel</button>
                <button onClick={handleDeleteAccount} className="py-4 rounded-2xl bg-red-600 text-white text-[10px] font-black uppercase">Terminate</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Profile;